import React, { memo, useCallback, useMemo } from 'react';
import { Star, Youtube, Wand2, X } from 'lucide-react';
import { BaseLanguageSelector, type Language, type QuickAction } from './BaseLanguageSelector';
import { CountryFlag } from '../../CountryFlag';
import { YOUTUBE_TOP_20_LANGUAGE_CODES } from '@/constants/youtubeLanguages';
import { useTranslation } from '@/hooks/useTranslation';
import type { YouTubeLanguageEntry } from '@/services/youtubePublishApi';

// Auto-detect sentinel — matches the backend "auto" handling in
// shared/routes/youtube_publish.py (publish + metadata/from-video).
const AUTO_DETECT_CODE = 'auto';
const TOP_LANGUAGES_LABEL = 'Top 20 Most Spoken';

export interface YouTubeLanguageSelectorProps {
  /** Selected language codes */
  values: string[];
  /** Callback when selection changes */
  onChange: (codes: string[]) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
  /** YouTube-supported languages fetched from GET /api/v1/youtube/languages */
  languages: YouTubeLanguageEntry[];
  /** Single-select mode (e.g. the source-language field). Default: multi-select. */
  maxSelection?: number;
  /** Show an auto-detect option (source-language field). Default: false. */
  showAutoDetect?: boolean;
  /** Detected-language info shown on the auto-detect chip. */
  detectedLanguageInfo?: { code: string; name: string; confidence: number } | null;
  /** Languages that already have caption tracks on the target video (#5 badge). */
  existingCaptionLanguages?: string[];
  /** Languages that already have SEO localizations on the target video (#5 badge). */
  existingLocalizationLanguages?: string[];
}

/**
 * YouTubeLanguageSelector — specialized language selector for the YouTube
 * Publish flow, built on the shared BaseLanguageSelector (same pattern + UX as
 * the AI Director's NLLBLanguageSelector).
 *
 * Multi-select (target languages): Top 20 / All / Clear quick actions, and an
 * optional per-language "captions ✓ / SEO ✓" badge when the selected video
 * already has those languages (so the user fills only the gaps).
 *
 * Single-select + auto-detect (source language): pass maxSelection={1} and
 * showAutoDetect. The "auto" sentinel is sent verbatim to the backend, which
 * detects the real language — never a silent fallback to English.
 */
export const YouTubeLanguageSelector = memo(function YouTubeLanguageSelector({
  values = [],
  onChange,
  disabled = false,
  className = '',
  languages: ytLanguages,
  maxSelection,
  showAutoDetect = false,
  detectedLanguageInfo,
  existingCaptionLanguages,
  existingLocalizationLanguages,
}: YouTubeLanguageSelectorProps) {
  const { t } = useTranslation();

  const safeValues = Array.isArray(values) ? values : [];
  const isSingleSelect = maxSelection === 1;

  // Map the backend YouTube language entries to the BaseLanguageSelector shape.
  const languages: Language[] = useMemo(
    () =>
      ytLanguages.map((lang) => ({
        code: lang.code,
        name: lang.name_en,
        nativeName: lang.native_name,
        region: lang.region,
      })),
    [ytLanguages]
  );

  const count = languages.length;

  // Top-20 codes filtered to those actually present in the catalog.
  const top20Codes = useMemo(() => {
    const available = new Set(languages.map((l) => l.code));
    return YOUTUBE_TOP_20_LANGUAGE_CODES.filter((c) => available.has(c));
  }, [languages]);

  const quickActions: QuickAction[] = useMemo(() => {
    const actions: QuickAction[] = [];
    if (top20Codes.length > 0) {
      actions.push({
        id: 'top20',
        label: t('languageSelector.quickActions.selectTop20Short', { count: top20Codes.length }),
        icon: <Star className="w-3 h-3" />,
        codes: top20Codes,
        className:
          'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/20',
      });
    }
    actions.push({
      id: 'all',
      label: t('youtube.publish.languages.selectAll', { count }),
      icon: <Youtube className="w-3 h-3" />,
      codes: languages.map((l) => l.code),
      className: 'bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20',
    });
    return actions;
  }, [t, count, languages, top20Codes]);

  // --- #5: per-language "already present" badges --------------------------
  const captionSet = useMemo(
    () => new Set(existingCaptionLanguages ?? []),
    [existingCaptionLanguages]
  );
  const localizationSet = useMemo(
    () => new Set(existingLocalizationLanguages ?? []),
    [existingLocalizationLanguages]
  );
  const hasStatus = captionSet.size > 0 || localizationSet.size > 0;

  const renderLanguageItem = useCallback(
    (lang: Language, isSelected: boolean, rank?: number): React.ReactNode => {
      const hasCaptions = captionSet.has(lang.code);
      const hasSeo = localizationSet.has(lang.code);
      return (
        <>
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={isSelected}
              readOnly
              tabIndex={-1}
              disabled={disabled}
              className="h-4 w-4 text-primary-500 border-white/20 rounded bg-black/20 focus:ring-primary-500 pointer-events-none"
              data-testid={`lang-${lang.code}`}
            />
            <CountryFlag code={lang.code} style={{ width: '24px', height: '24px' }} />
            <div className="text-left">
              <div className="text-sm font-medium text-white">
                {lang.name}
                {rank !== undefined && (
                  <span className="ml-1 text-xs bg-yellow-500/20 text-yellow-400 px-1 rounded">
                    #{rank + 1}
                  </span>
                )}
              </div>
              <div className="text-xs text-white/50">{lang.nativeName}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {hasCaptions && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 border border-green-500/30">
                {t('youtube.publish.languages.captionsPresent')}
              </span>
            )}
            {hasSeo && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/30">
                {t('youtube.publish.languages.seoPresent')}
              </span>
            )}
            <span className="text-xs text-white/30 uppercase">{lang.code}</span>
          </div>
        </>
      );
    },
    [captionSet, localizationSet, disabled, t]
  );

  // --- #3: auto-detect (source-language) handling -------------------------
  const isAutoDetectSelected = safeValues.includes(AUTO_DETECT_CODE);
  const baseValues = safeValues.filter((v) => v !== AUTO_DETECT_CODE && v !== '');

  const handleAutoDetectToggle = useCallback(() => {
    if (disabled) return;
    onChange(isAutoDetectSelected ? [''] : [AUTO_DETECT_CODE]);
  }, [disabled, isAutoDetectSelected, onChange]);

  const handleClearAutoDetect = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      onChange(['']);
    },
    [disabled, onChange]
  );

  return (
    <div className={className}>
      {/* Regular selector — hidden when auto-detect is active */}
      {!isAutoDetectSelected && (
        <BaseLanguageSelector
          languages={languages}
          values={baseValues}
          onChange={onChange}
          disabled={disabled}
          maxSelection={maxSelection}
          showQuickActions={!isSingleSelect}
          quickActions={quickActions}
          topLanguageCodes={top20Codes}
          topLanguagesLabel={TOP_LANGUAGES_LABEL}
          renderLanguageItem={hasStatus ? renderLanguageItem : undefined}
          searchPlaceholder={t('youtube.publish.languages.searchPlaceholder', { count })}
          footerDescription={t('youtube.publish.languages.footer', { count })}
          testId="youtube-language-selector"
        />
      )}

      {/* Auto-detect selected chip */}
      {isAutoDetectSelected && (
        <div className="flex items-center flex-wrap gap-1 mb-2">
          <span className="inline-flex items-center px-2 py-1 bg-purple-900/30 border border-purple-500/40 text-purple-200 text-xs rounded-md">
            <Wand2 className="w-3 h-3 mr-1" />
            {t('languageSelector.autoDetect.label')}
            {detectedLanguageInfo && (
              <span className="ml-1 text-purple-300">
                ({detectedLanguageInfo.name} - {Math.round(detectedLanguageInfo.confidence * 100)}%)
              </span>
            )}
            <button
              type="button"
              onClick={handleClearAutoDetect}
              disabled={disabled}
              className="ml-2 p-0.5 rounded-full hover:bg-purple-800/60 transition-colors"
              title={t('languageSelector.autoDetect.clear')}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        </div>
      )}

      {/* Auto-detect option checkbox (source-language field) */}
      {showAutoDetect && (
        <div className="mt-2">
          <label
            className={`w-full px-3 py-3 flex items-center justify-between cursor-pointer rounded-lg border transition-colors ${
              isAutoDetectSelected
                ? 'bg-purple-900/20 border-purple-500/30'
                : 'border-white/10 hover:bg-purple-900/10'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={isAutoDetectSelected}
                onChange={handleAutoDetectToggle}
                disabled={disabled}
                className="h-4 w-4 text-purple-600 border-white/20 rounded bg-black/20 focus:ring-purple-500"
              />
              <Wand2 className="w-5 h-5 text-purple-400" />
              <div className="text-left">
                <div className="text-sm font-medium text-white">
                  {t('languageSelector.autoDetect.label')}
                </div>
                <div className="text-xs text-white/50">
                  {t('languageSelector.autoDetect.description')}
                </div>
              </div>
            </div>
            <span className="text-xs text-purple-400 font-medium uppercase">
              {t('languageSelector.autoDetect.badge')}
            </span>
          </label>
        </div>
      )}
    </div>
  );
});

export default YouTubeLanguageSelector;
